import { Fragment, useState } from "react";
import { Transition, Menu, Dialog, Disclosure } from "@headlessui/react";
import Rive, { Alignment, Fit, Layout } from "@rive-app/react-canvas-lite";
import {
  ChevronDownIcon,
  GlobeAltIcon,
  ArrowRightOnRectangleIcon,
  DocumentTextIcon,
  TruckIcon,
  Bars3Icon,
  ComputerDesktopIcon,
  PlusIcon,
  CalculatorIcon,
  DocumentCheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import * as React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { NavLink } from "react-router-dom";
import { roles } from "utils/constant";
import { Input } from "components/Input";
import { Button } from "components/Button";
import { GetCommanWay, ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import { postLogout } from "service/api";
import UseToast from "utils/AfToast";
import { useAuthStore } from "store/auth";
import { log } from "node:console";
import { Img } from "components/Img";
import TotpSetup from "./TotpSetup";
import Todo from "./Todo";
import Notification from "./Notification";
import { BellIcon, FingerPrintIcon } from "@heroicons/react/24/outline";
import { isWebAuthnSupported, isPlatformAuthAvailable, registerFingerprint } from "utils/webauthn";
import { CommandRelayButton } from "components/CommandRelay";
import BankBalanceModal from "./BankBalanceModal";

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

export default function Navbar() {
  const [userDetails, setUserDetails] = useState<any>({});
  const [branchData, setBranchData] = useState<any>({});
  const [subbranchData, setSubbranchData] = useState<any>({});

  const [totpPopup, setTotpPopup] = useState<any>(false);
  const [changePassPopup, setChangePassPopup] = useState(false);
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");

  const [isCalcOpen, setIsCalcOpen] = useState(false);
  const [baseAmt, setBaseAmt] = useState(0);
  const [gstPercent, setGstPercent] = useState(18);

  const [isTodoOpen, setIsTodoOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isBankBalanceOpen, setIsBankBalanceOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [fingerprintLoading, setFingerprintLoading] = useState(false);
  const [fingerprintAvailable, setFingerprintAvailable] = useState(false);

  // Check if today is the user's birthday (compare MM-DD)
  const isBirthdayToday = (() => {
    if (!userDetails.dob) return false;
    const today = new Date();
    const [, mm, dd] = userDetails.dob.split('-');
    return parseInt(mm) === today.getMonth() + 1 && parseInt(dd) === today.getDate();
  })();

  const navigator = useNavigate();
  const { setToken, setUser, token, removeAll } = useAuthStore();

  // Check if platform authenticator (fingerprint) is available
  React.useEffect(() => {
    isPlatformAuthAvailable().then(setFingerprintAvailable);
  }, []);

  const handleEnableFingerprint = async () => {
    if (!token) {
      UseToast("You must be logged in", "error");
      return;
    }
    setFingerprintLoading(true);
    try {
      const result = await registerFingerprint(token);
      UseToast(result.message, "success");
    } catch (err: any) {
      UseToast(err.message || "Fingerprint registration failed", "error");
    } finally {
      setFingerprintLoading(false);
    }
  };

  const {
    mutate,
    isLoading,
    data: data_,
  } = useMutation(
    (data: any) =>
      ListCommanWay(
        data.url,
        {
          id: data.id,
        },
        {}
      ),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          setBranchData(data.data.data[0]);
        }
      },
      onError: (data: any) => {},
    }
  );

  React.useEffect(() => {
    const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
    if (Object.keys(userD).length) {
      setUserDetails(userD.state.user);
      if (userD.state.user.branch_name) {
        mutate({
          type: "branch",
          url: "/admin/branch/list",
          id: userD.state.user.branch_name,
        });
      } else if (userD.state.user.subbranch) {
        mutate({
          type: "subbranch",
          url: "/admin/subbranch/list",
          id: userD.state.user.subbranch,
        });
      }
    }
  }, []);

  // Fetch unread notification count
  useQuery(
    ["UnreadNotificationCount"],
    () =>
      ListCommanWay(
        "/admin/notification/list",
        { isRead: false },
        { isCountOnly: true }
      ),
    {
      keepPreviousData: true,
      refetchInterval: 60000, // Refresh every 60 seconds
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          setUnreadCount(data.data?.totalRecords || data.data?.count || data.data?.data?.length || 0);
        }
      },
    }
  );

  const THIRD_PARTY_PERMISSION = [""];

  const navigation: any = [
    ...(userDetails.roleName === roles.SUPER_ADMIN || userDetails.roleName === roles.CA_READ_ONLY
      ? [
          {
            name: "ADMIN",
            current: false,
            columns: [
              // Column 1
              [
                /*{ name: "Branch", to: "/master/branch/list" },*/
                { name: "Wallets", to: "/master/wallet/list" },
                { name: "Customer", to: "/master/customer/list" },
                { name: "Rate", to: "/master/rate/list" },
                /*{ name: "State", to: "/master/state/list" },*/
                { name: "City", to: "/master/city/list" },
                { name: "Pincode", to: "/master/pincode/list" },
                { name: "Branch", to: "/master/subbranch/list" },
                { name: "Non-Branch Contact", to: "/master/nonbranchcontact/list" },
                /*{ name: "Product", to: "/master/product/list" },
                { name: "Employee", to: "/master/agent/list" },*/
                { name: "Airfield Users", to: "/master/user/list?tp=0" },
                { name: "ThirdPart Users", to: "/master/user/list?tp=1" },
              ],
              // Column 2
              [
                { name: "Users Security", to: "/security/user/list" },
                { name: "Data Change", to: "/admin/data-change" },
                { name: "Regular Pickup", to: "/admin/regular-pickup" },
                { name: "Leave Approvals", to: "/report/leave" },
                { name: "Employee Quota", to: "/admin/leave-quota" },
                { name: "Leave Policy", to: "/admin/leave-policy" },
                { name: "Geo Track", to: "/geo/list" },
                { name: "RAG AI Q&A", to: "/admin/rag-question" },
                { name: "Bank Balance", onClick: () => setIsBankBalanceOpen(true) },
                { name: "TOOLS", to: "/admin/tools" },
              ],
            ],
          },
        ]
      : []),

    ...(userDetails.roleName === "CUSTOMER"
      ? []
      : [
          {
            name: "Sales",
            current: false,
            children: [

              
              ...(userDetails.username?.toLowerCase() == "anilp" ? [{ name: "Customer", to: "/master/customer/list" }] : []),
              ...(userDetails.username?.toLowerCase() != "anilp" ? [{ name: "Lead Suggest", to: "/af/lead-suggestions" }] : []),

              ...(userDetails.username?.toLowerCase() == "anilp" || userDetails.roleName === roles.SUPER_ADMIN || userDetails.roleName === roles.CA_READ_ONLY
                ? [{ name: "Leads", to: "/af/leads" }]
                : []),

              { name: "Letterhead", to: "/af/letterhead" },
            ],
          },
        ]),

    ...(userDetails.roleName === roles.SUPER_ADMIN || userDetails.roleName === roles.CA_READ_ONLY
      ? [
          {
            name: "CareX",
            current: false,
            to: "/tickets/assigned",
          },
        ]
      : []),
    /*...(userDetails.roleName === roles.SUPER_ADMIN ||
    userDetails.roleName === roles.BRANCH_ADMIN ||
    userDetails.roleName === roles.SUBBRANCH_ADMIN
      ? [
          {
            name: "MASTER",
            icon: Bars3Icon,
            current: false,
            children: [{ name: "Customer", to: "/master/customer/list" }],
          },
        ]
      : []),*/

    ...(userDetails.roleName === roles.SUPER_ADMIN || userDetails.roleName === roles.CA_READ_ONLY || userDetails.roleName === roles.MANAGER
      ? [
          {
            name: "Account",
            icon: Bars3Icon,
            current: false,
            children: [
              { name: "Vendor Master", to: "/master/vendor/list" },
              { name: "Vendor Invoice/Payment", to: "/master/vendorpayment/list" },
              { name: "Vendor Invoice Verify", to: "/billing/vendor-invoice" },
              { name: "Expense Vouchers", to: "/vouchure/expense/list" },
              { name: "Fuel Vouchers", to: "/billing/vouchure-gen" },
              { name: "Salary", to: "/master/salary/list" },
              { name: "Company Expense", to: "/master/companyexpense/list" },
              { name: "Purchase Order", to: "/purchase-order/list" },
              { name: "Assets", to: "/asset/list" },
              { name: "Expense Report", to: "/report/expense" },
              ...(userDetails.roleName === roles.SUPER_ADMIN || userDetails.roleName === roles.CA_READ_ONLY ? [{ name: "GSTR-2B Recon", to: "/billing/gstr2b" }] : []),
              ...(userDetails.roleName === roles.SUPER_ADMIN ? [{ name: "Initiated Payments", to: "/account/initiated-payments" }] : []),
            ],
          },
        ]
      : []),
    ...(userDetails.roleName === roles.SUPER_ADMIN ||
    userDetails.roleName === roles.CA_READ_ONLY ||
    userDetails.roleName === roles.BRANCH_ADMIN ||
    userDetails.roleName === roles.SUBBRANCH_ADMIN ||
    userDetails.roleName === roles.AGENT ||
    userDetails.roleName === roles.CUSTOMER_SERVICE
      ? [
          {
            name: "BOOKING",
            icon: TruckIcon,
            current: false,
            children: [
              /*{ name: "AWB List", to: "/awb/list" },*/

              /*...(userDetails.userType == 9 ? [] : [{ name: "Pickup Requests", to: "/topay-pickup/req" }]),*/
              ...(userDetails.userType == 9 ? [] : [{ name: "Topay Pickup", to: "/topay-pickup/add" }]),
              { name: "Book Domestic", to: "/booking/add" },
              { name: "Book International", to: "/booking/add-intl" },

              ...(userDetails.userType == 9
                ? []
                : [
                    { name: "Rate Calculator", to: "/rate/calculate" },
                    { name: "Intl. Rate Calculator", to: "/rate/intl-calculate" },
                  ]),
            ],
          },
          {
            name: "OPERATION",
            icon: TruckIcon,
            current: false,
            children: [
              ...(userDetails.userType == 9 || userDetails.userType == 4 ? [] : [{ name: "AWB Sticker", to: "/awb/generate" }]),
              ...(userDetails.userType == 9 ? [] : [{ name: "AWB Slip Generator", to: "/awb/open-slip" }]),
              { name: "Flight Schedule", to: "/report/flight" },
              { name: "Train Schedule", to: "/report/train" },

              { name: "Mother Bag Tally", to: "/mother_bag/add" },
              /*{ name: "List Mother Bag Tally", to: "/mother_bag/list" },*/

              { name: "Out Scan Motherbag", to: "/mother_bag/outscan" },
              { name: "In Scan Motherbag", to: "/mother_bag/inscan" },
              /*{ name: "Mother Bag In-Scan V2", to: "/mother_bag/inscan-v2" },*/

              { name: "Generate DRS", to: "/drs/manage" },
              { name: "List  DRS", to: "/drs/list" },

              { name: "Send to Thirdparty", to: "/third-party/manage" },

              { name: "Update POD", to: "/pod/update" },
              { name: "Box QR Tracker", to: "/operation/box-tracker" },
              { name: "Overedge", to: "/pod/overedge" },

              ...(userDetails.roleName === roles.SUPER_ADMIN || userDetails.roleName === roles.CA_READ_ONLY || userDetails.roleName === roles.CUSTOMER_SERVICE
                ? [{ name: "Regular Pickup", to: "/admin/regular-pickup" }]
                : []),

              ...(userDetails.roleName === roles.SUPER_ADMIN || userDetails.roleName === roles.CA_READ_ONLY || userDetails.username?.toLowerCase() === "lovishs"
                ? [{ name: "Stock Report", to: "/report/stock" }]
                : []),
            ],
          },
          {
            name: "REPORT",
            icon: TruckIcon,
            current: false,
            children: [
              { name: "Booking", to: "/report/booking" },

              ...(userDetails.roleName === roles.SUPER_ADMIN || userDetails.roleName === roles.CA_READ_ONLY
                ? [
                    { name: "Vendor Master", to: "/master/vendor/list" },
                    { name: "Vendor Invoice/Payment", to: "/master/vendorpayment/list" },
                    { name: "Vendor Invoice Verify", to: "/billing/vendor-invoice" },
                  ]
                : []),

              ...(userDetails.userType == 9
                ? []
                : [
                    { name: "Cash Report", to: "/report/cash" },
                    { name: "Credit Report", to: "/report/credit" },
                    { name: "Invoice Report", to: "/report/invoice" },
                    ...((userDetails.roleName === roles.SUPER_ADMIN || userDetails.roleName === roles.CA_READ_ONLY || userDetails.roleName === roles.MANAGER)
                      ? [
                          { name: "Cash Invoice", to: "/billing/cash-invoice" },
                          { name: "CN / DN", to: "/billing/cn-dn" },
                        ]
                      : []),
                    { name: "Attendance", to: "/report/attendance" },
                    { name: "Leave", to: "/report/leave" },
                    { name: "Delivery Report", to: "/report/emp-delivery" },
                    { name: "Expense Voucher", to: "/vouchure/expense/list" },
                    { name: "Fuel Voucher", to: "/billing/vouchure-gen" },
                  ]),
              ...(userDetails.roleName === roles.AGENT && userDetails.userType != 9
                ? [{ name: "Travel Voucher", to: "/billing/vouchure-gen" }]
                : []),

              ...(userDetails.roleName === roles.SUPER_ADMIN || userDetails.roleName === roles.CA_READ_ONLY
                ? [
                    { name: "Collection", to: "/report/collection" },
                    /*{ name: "Outstanding", to: "/report/outstanding" },
                    { name: "Statement", to: "/report/statement" },*/
                  ]
                : []),

                ...(userDetails.username?.toLowerCase() == "anilp" || userDetails.roleName === roles.SUPER_ADMIN || userDetails.roleName === roles.CA_READ_ONLY
                ? [{ name: "Last 6 Months", to: "/report/last-6-months" },
                  { name: "Product Wise Report", to: "/report/product-wise" }
                ]
                : []),
            ],
          },
        ]
      : []),
    ...(userDetails.roleName === roles.MIS_CREDIT_CASH || userDetails.roleName === roles.MANAGER
      ? [
          {
            name: "BOOKING",
            icon: TruckIcon,
            current: false,
            children: [{ name: "Topay Pickup", to: "/topay-pickup/add" }],
          },
        ]
      : []),

    ...(userDetails.roleName === roles.ACCOUNTANT
      ? [
          {
            name: "Report",
            icon: TruckIcon,
            current: false,
            children: [
              { name: "Expense Voucher", to: "/vouchure/expense/list" },
              { name: "Fuel Voucher", to: "/billing/vouchure-gen" },
              { name: "Leave", to: "/report/leave" },
            ],
          },
        ]
      : []),
    ...(userDetails.roleName === roles.MANAGER
      ? [
          {
            name: "Report",
            icon: TruckIcon,
            current: false,
            children: [
              { name: "Customer", to: "/master/customer/list" },
              { name: "Booking", to: "/report/booking" },
              { name: "Cash Report", to: "/report/cash" },
              { name: "Attendance", to: "/report/attendance" },
              { name: "Leave", to: "/report/leave" },
              { name: "Expense Voucher", to: "/vouchure/expense/list" },
              { name: "Fuel Voucher", to: "/billing/vouchure-gen" },
              { name: "Vendor Master", to: "/master/vendor/list" },
              { name: "Vendor Invoice/Payment", to: "/master/vendorpayment/list" },
              { name: "Vendor Invoice Verify", to: "/billing/vendor-invoice" },
              { name: "Out Scan Motherbag", to: "/mother_bag/outscan" },
            ],
          },
        ]
      : []),

    ...(userDetails.roleName === roles.MIS_CREDIT_CASH
      ? [
          {
            name: "Report",
            icon: TruckIcon,
            current: false,
            children: [
              { name: "Booking", to: "/report/booking" },
              { name: "Cash Report", to: "/report/cash" },
              { name: "Credit Report", to: "/report/credit" },
              { name: "Invoice Report", to: "/report/invoice" },
              { name: "Attendance", to: "/report/attendance" },
              { name: "Leave", to: "/report/leave" },
              { name: "Expense Voucher", to: "/vouchure/expense/list" },
            ],
          },
        ]
      : []),
    /*...(userDetails.roleName === roles.SUPER_ADMIN || 
      userDetails.roleName === roles.ACCOUNTANT || 
      userDetails.roleName === roles.MANAGER
      ? [
          {
            name: "Billing",
            icon: Bars3Icon,
            current: false,
            children: [
              { name: userDetails.roleName === roles.AGENT ? "Travel Voucher" : "Generate Voucher", to: "/billing/vouchure-gen" },
            ],
          },
        ]
      : []),*/

    ...(userDetails.roleName === "CUSTOMER"
      ? [
          {
            name: "Book Domestic",
            icon: PlusIcon,
            current: false,
            to: "/booking/add",
          },
          {
            name: "Booking Report",
            icon: TruckIcon,
            current: false,
            to: "/customer-report/booking",
          },
        ]
      : []),
  ];

  const logout = () => {
    postLogout({ data: {}, headers: { Authorization: "Bearer " + token } })
      .then((res) => {
        UseToast("Logout Successfull", "success");
        localStorage.removeItem("af_token");
        removeAll();
        navigator("/login");
      })
      .catch((err) => {
        console.error(err);
        UseToast("Login Failed! Try Again", "error");
      });
  };

  const { mutate: mutateChangePass, isLoading: isChangePassLoading } = useMutation(
    (data: any) =>
      UpdateCommanWay({
        url: data.url,
        data: { oldPassword: oldPass, newPassword: newPass },
      }),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          //setBranchData(data.data.data[0]);
          UseToast("Password Changed Successfully", "success");
          logout();
        } else {
          UseToast(data?.message || "Something went wrong", "error");
        }
      },
      onError: (data: any) => {
        UseToast(data?.message || "Something went wrong", "error");
      },
    }
  );

  const onChangePass = () => {
    if (newPass == "" || confirmPass == "" || oldPass == "") {
      UseToast("All fields are required", "error");
      return;
    }
    if (newPass != "" && newPass !== confirmPass) {
      UseToast("Password and Confirm Password does not match", "error");
      return;
    }
    if (newPass.length < 8) {
      UseToast("Password should be atleast 8 characters", "error");
      return;
    }
    mutateChangePass({
      type: "change_pass",
      url: "/admin/user/change-password",
    });
  };

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location: any = useLocation();
  const params = useParams();
  const id: string | undefined = params?.id;

  let pathName = location?.pathname;
  try {
    let id_: any = location?.state?.state;
    if (!id_) {
      id_ = location?.state?.id;
    }
    if (id_) {
      pathName = pathName?.replaceAll("/edit/" + id_, "");
      pathName = pathName?.replaceAll("/" + id_, "");
    }
  } catch (ignore) {}

  const getNavColumns = (item: any) => {
    if (item?.columns && Array.isArray(item.columns)) {
      return item.columns;
    }
    if (item?.column1 && item?.column2) {
      return [item.column1, item.column2];
    }
    if (item?.children && Array.isArray(item.children) && item.children.some((c: any) => c.col || c.column)) {
      const col1 = item.children.filter((c: any) => (c.col || c.column || 1) === 1);
      const col2 = item.children.filter((c: any) => (c.col || c.column) === 2);
      return [col1, col2];
    }
    return null;
  };

  const getNavChildren = (item: any) => {
    const cols = getNavColumns(item);
    if (cols) {
      return cols.flat();
    }
    return item?.children || null;
  };

  const renderDropdownItem = (subItem: any, parentName: string) => (
    <Menu.Item key={parentName + subItem.name}>
      {({ active }) =>
        subItem.onClick ? (
          <button
            type="button"
            className={`${
              active ? "bg-dark1 text-white" : "text-gray-900"
            } group flex w-full items-center rounded-md px-2.5 py-2 text-sm font-medium text-left whitespace-nowrap`}
            onClick={() => subItem.onClick()}
          >
            {subItem.name}
          </button>
        ) : (
          <Link
            className={`${
              active ? "bg-dark1 text-white" : "text-gray-900"
            } group flex w-full items-center rounded-md px-2.5 py-2 text-sm font-medium whitespace-nowrap`}
            to={subItem.to}
            onClick={() => {
              if (location.pathname + location.search === subItem.to) {
                setTimeout(() => {
                  navigator(subItem.to, { state: { key: Date.now() } });
                }, 0);
              }
            }}
          >
            {subItem.name}
          </Link>
        )
      }
    </Menu.Item>
  );

  const sideNavigation = navigation.map((item: any) => {
    const itemCols = getNavColumns(item);
    const navChildren = getNavChildren(item);

    return (
      <div key={"main-" + item.name}>
        {!navChildren ? (
          <div key={item.name}>
            <NavLink
              to={item?.to}
              onClick={(e) => {
                setSidebarOpen(false);
                if (location.pathname + location.search === item?.to) {
                  e.preventDefault();
                  navigator(item?.to, { state: { key: Date.now() } });
                }
              }}
              key={"navlink" + item.name}
              className={(props) => {
                return classNames(
                  props?.isActive && "bg-grey_bg",
                  " justify-center rounded-md px-4 py-2 text-sm font-semibold font-quicksand   focus:outline-none  bg-[#ffffff] relative inline-block text-left mr-3"
                );
              }}
            >
              {item.name}
            </NavLink>
          </div>
        ) : (
          <Menu key={item.name} as="div" className="relative inline-block text-left mr-3">
            <div>
              <Menu.Button className="inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold font-quicksand   focus:outline-none  bg-[#ffffff]">
                {item.iconn && <GlobeAltIcon className="mr-2 h-5 w-5" aria-hidden="true" />}
                {item.name}
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              {itemCols ? (
                <Menu.Items className="absolute z-10 mt-2 w-[460px] origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none left-0 p-2">
                  <div className="grid grid-cols-2 divide-x divide-gray-100">
                    <div className="flex flex-col space-y-0.5 pr-2">
                      {itemCols[0]?.map((subItem: any) => renderDropdownItem(subItem, item.name))}
                    </div>
                    <div className="flex flex-col space-y-0.5 pl-2">
                      {itemCols[1]?.map((subItem: any) => renderDropdownItem(subItem, item.name))}
                    </div>
                  </div>
                </Menu.Items>
              ) : (
                <Menu.Items className="absolute z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none left-0">
                  {item.children.map((subItem: any) => renderDropdownItem(subItem, item.name))}
                </Menu.Items>
              )}
            </Transition>
          </Menu>
        )}
      </div>
    );
  });

  // Mobile navigation - uses the same navigation data
  const mobileNavigation = navigation.map((item: any) => {
    const navChildren = getNavChildren(item);

    return (
      <div key={"mobile-" + item.name}>
        {!navChildren ? (
          <NavLink
            to={item?.to}
            onClick={(e) => {
              setMobileMenuOpen(false);
              if (location.pathname + location.search === item?.to) {
                e.preventDefault();
                navigator(item?.to, { state: { key: Date.now() } });
              }
            }}
            className={(props) =>
              classNames(
                props?.isActive ? "bg-primary text-white" : "text-gray-900 hover:bg-gray-100",
                "block rounded-md px-3 py-2 text-base font-medium"
              )
            }
          >
            {item.name}
          </NavLink>
        ) : (
          <Disclosure as="div" key={item.name}>
            {({ open }) => (
              <>
                <Disclosure.Button className="flex w-full items-center justify-between rounded-md px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100">
                  {item.name}
                  <ChevronDownIcon className={classNames(open ? "rotate-180" : "", "h-5 w-5 transition-transform")} />
                </Disclosure.Button>
                <Disclosure.Panel className="pl-4">
                  {navChildren.map((subItem: any) =>
                    subItem.onClick ? (
                      <button
                        key={item.name + subItem.name}
                        type="button"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          subItem.onClick();
                        }}
                        className="block w-full text-left rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                      >
                        {subItem.name}
                      </button>
                    ) : (
                      <NavLink
                        key={item.name + subItem.name}
                        to={subItem.to}
                        onClick={(e) => {
                          setMobileMenuOpen(false);
                          if (location.pathname + location.search === subItem.to) {
                            e.preventDefault();
                            navigator(subItem.to, { state: { key: Date.now() } });
                          }
                        }}
                        className={(props) =>
                          classNames(
                            props?.isActive ? "bg-primary/10 text-primary" : "text-gray-600 hover:bg-gray-50",
                            "block rounded-md px-3 py-2 text-sm font-medium"
                          )
                        }
                      >
                        {subItem.name}
                      </NavLink>
                    )
                  )}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        )}
      </div>
    );
  });

  const [trackValue, setTrackValue] = useState("");
  return (
    <div key={"Asd"} className={`${pathName?.includes("/drs/list") ? "print:hidden" : "print:hidden"}`}>
      {isTodoOpen && (
        <div>
          <div className="fixed left-0 top-0 w-full h-full bg-black-900/20 z-[1001]">
            <div
              className="absolute left-0 top-0 w-full h-full z-10"
              onClick={() => {
                setIsTodoOpen(false);
              }}
            ></div>
            <div className="max-w-[500px] ml-auto bg-white rounded-lg shadow-xl p-4 z-20 relative h-[96%] mr-[1%] mt-[1%] overflow-auto">
              <Todo />
            </div>
          </div>
        </div>
      )}

      {isNotificationOpen && (
        <div>
          <div className="fixed left-0 top-0 w-full h-full bg-black-900/20 z-[1001]">
            <div
              className="absolute left-0 top-0 w-full h-full z-10"
              onClick={() => {
                setIsNotificationOpen(false);
              }}
            ></div>
            <div className="max-w-[500px] ml-auto bg-white rounded-lg shadow-xl p-4 z-20 relative h-[96%] mr-[1%] mt-[1%] overflow-auto">
              <Notification onClose={() => setIsNotificationOpen(false)} />
            </div>
          </div>
        </div>
      )}

      <BankBalanceModal isOpen={isBankBalanceOpen} onClose={() => setIsBankBalanceOpen(false)} />

      {isCalcOpen && (
        <div className="fixed left-0 top-0 w-full h-full bg-black-900/20 z-[1001]">
          <div
            className="absolute left-0 top-0 w-full h-full z-10"
            onClick={() => {
              setIsCalcOpen(false);
            }}
          ></div>
          <div className="max-w-[500px] ml-auto mt-16 bg-white rounded-lg shadow-xl p-6 z-20 relative">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Inclusive Tax Calculator</h2>
              <button onClick={() => setIsCalcOpen(false)} className="text-gray-500 hover:text-gray-700 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex gap-4 items-center">
              <div>
                <label className="block text-sm font-medium text-gray-700 ">Grand Total</label>
                <Input
                  type="number"
                  placeholder="Grand Total"
                  className="w-full border-2 border-gray-300 focus:border-primary"
                  value={baseAmt}
                  onChange={(e) => setBaseAmt(e)}
                />
              </div>
              =
            </div>

            <div className="flex items-center gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 ">Tax</label>
                <Input
                  type="number"
                  placeholder="Enter Tax"
                  className="border-2 border-gray-300 w-20 focus:border-primary"
                  value={gstPercent}
                  onChange={(e) => setGstPercent(e.target.value)}
                />
              </div>
              +
              <div>
                <label className="block text-sm font-medium text-gray-700 ">Base Amount</label>
                <Input
                  type="number"
                  disabled
                  className="border-2 border-gray-300 focus:border-primary"
                  value={((baseAmt * 100) / (100 + parseFloat(gstPercent + ""))).toFixed(2)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* responsive */}
      {totpPopup && (
        <div className="absolute z-50 left-0 top-0 w-full h-full min-h-screen bg-black-900 bg-opacity-60 flex items-start justify-center">
          <div className="rounded-xl border-2 border-primary p-4 bg-white relative mt-24 min-w-96">
            <Button
              className="bg-red-600 px-4 py-2 rounded-md mb-4 text-white"
              onClick={() => {
                setTotpPopup(false);
              }}
            >
              Close
            </Button>
            <TotpSetup />
          </div>
        </div>
      )}

      <div className="border-b- p-1.5- bg-[#E4EBF5]">
        {/* ============================================================
            THEME BACKGROUNDS — uncomment ONE at a time:
            - blue-grad        = Default blue
            - birthday-grad    = Birthday (dark purple, contrasts with emojis)
            - winter-bg        = Christmas / Winter red
           ============================================================ */}
        <div className={`flex flex-grow ${isBirthdayToday ? 'birthday-grad' : 'blue-grad'} rounded-md- relative h-[60px] z-[99]`}>

          {/* ── Snowflake Effect (uncomment to enable) ──
          <div className="snowfall-container">
            {[...Array(20)].map((_, i) => (
              <span key={i} className="snowflake">❄</span>
            ))}
          </div>
          ── End Snowflake ── */}

          {/* ── Diwali Fire Effect (uncomment to enable) ──
          <iframe src="https://api.airfield.express/fire" className="absolute left-0 top-0 right-0 bottom-0 w-full h-full" />
          ── End Diwali ── */}

          {/* ── Birthday Celebration Effect (auto-shows on user's birthday) ── */}
          {isBirthdayToday && (
            <div className="birthday-container">
              {['🎉', '🎊', '✨', '🎀', '💫', '🎉', '🎊', '✨', '🎀', '💫', '🎉', '🎊', '✨', '🎀', '💫'].map((emoji, i) => (
                <span key={`confetti-${i}`} className="confetti">{emoji}</span>
              ))}
              {['🎈', '🎈', '🎈', '🎈', '🎈', '🎈', '🎈'].map((emoji, i) => (
                <span key={`balloon-${i}`} className="balloon">{emoji}</span>
              ))}
              {['⭐', '✨', '🌟', '✨', '⭐', '🌟'].map((emoji, i) => (
                <span key={`sparkle-${i}`} className="sparkle">{emoji}</span>
              ))}
            </div>
          )}
          {/* ── End Birthday ── */}

          {/* Mobile menu button */}
          <div className="hidden md:flex items-center px-2 z-10">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-md text-white hover:bg-white/10"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex flex-shrink-0 items-center px-2 z-10">
            <Link to="/dashboard">
              <Img src={"/logo-white.png"} className="h-[24px] ml-auto" />
            </Link>
          </div>

          {/* Desktop navigation */}
          <nav className="flex md:hidden flex-grow items-center flex-1 px-2 overflow-y-visible z-10 font-inter" aria-label="Sidebar">
            {sideNavigation}

            <div className="flex items-center mr-3 bg-white rounded-full pl-2 pr-1 py-0.5 shadow-sm group focus-within:ring-2 focus-within:ring-blue-400">
              <Input
                type="text"
                className="p-1 bg-transparent font-RobotoCondensed font-semibold max-w-[130px] border-none outline-none focus:outline-none focus:ring-0 text-gray-800 placeholder-gray-400"
                placeholder="Track AWB"
                value={trackValue}
                onChange={(e) => {
                  setTrackValue(e);
                }}
              />
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full h-7 w-7 flex items-center justify-center flex-shrink-0 transition-colors"
                onClick={() => {
                  if (trackValue.length < 10 && userDetails.roleName != "CUSTOMER") {
                    navigator("/pickup/tracking?awb=" + trackValue);
                  } else {
                    navigator((userDetails.roleName == "CUSTOMER" ? "/customer-pod/tracking?awb=" : "/pod/tracking?awb=") + trackValue);
                  }

                  setTrackValue("");
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => logout()}
                className={
                  "flex items-center justify-center text-white font-medium rounded-full h-8 w-8 border-2 bg-red-600/60 border-red-600"
                }
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="#ffffff " className="size-4">
                  <path
                    fillRule="evenodd"
                    d="M8 1a.75.75 0 0 1 .75.75v6.5a.75.75 0 0 1-1.5 0v-6.5A.75.75 0 0 1 8 1ZM4.11 3.05a.75.75 0 0 1 0 1.06 5.5 5.5 0 1 0 7.78 0 .75.75 0 0 1 1.06-1.06 7 7 0 1 1-9.9 0 .75.75 0 0 1 1.06 0Z"
                    clipRule="evenodd"
                  />
                </svg>
              </Button>

              {userDetails.roleName != "CUSTOMER" && (
                <Button
                  onClick={() => setIsCalcOpen(true)}
                  className={
                    "flex items-center justify-center text-white font-medium rounded-md h-8 w-8 border-2 p-1 bg-primary/50 border-primary"
                  }
                >
                  <CalculatorIcon />
                </Button>
              )}

              {/* Command Relay Button */}
              {userDetails.roleName != "CUSTOMER" && (
                <CommandRelayButton />
              )}

              {userDetails.roleName != "CUSTOMER" && <Button
                onClick={() => setIsTodoOpen(true)}
                className={
                  "flex items-center justify-center text-white font-medium rounded-md h-8 w-8 border-2 p-1 bg-yellow-400/50 border-yellow-400 relative"
                }
              >
                <DocumentCheckIcon />
              </Button>}
            </div>
          </nav>

          {/* Mobile action buttons - shown on mobile */}
          <div className="hidden md:flex items-center gap-2 ml-auto px-2 z-10">
            <Button
              onClick={() => logout()}
              className={
                "flex items-center justify-center text-white font-medium rounded-full h-8 w-8 border-2 bg-red-600/60 border-red-600"
              }
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="#ffffff " className="size-4">
                <path
                  fillRule="evenodd"
                  d="M8 1a.75.75 0 0 1 .75.75v6.5a.75.75 0 0 1-1.5 0v-6.5A.75.75 0 0 1 8 1ZM4.11 3.05a.75.75 0 0 1 0 1.06 5.5 5.5 0 1 0 7.78 0 .75.75 0 0 1 1.06-1.06 7 7 0 1 1-9.9 0 .75.75 0 0 1 1.06 0Z"
                  clipRule="evenodd"
                />
              </svg>
            </Button>
          </div>

          <div className="flex md:hidden items-center justify-end px-2 my-2 gap-2">
            {/* Happy Birthday Text — auto-shows on user's birthday */}
            {isBirthdayToday && (
              <span className="birthday-text-inline font-RobotoCondensed z-10 flex-shrink-0">🎂 Happy Birthday! 🎂</span>
            )}
            {/* ── End Birthday Text ── */}
            {userDetails.roleName != "CUSTOMER" && (
              <Button
                onClick={() => setIsNotificationOpen(true)}
                className={`flex items-center justify-center text-white font-medium rounded-md h-8 w-8 border-2 p-1 bg-white/20 border-white/40 relative flex-shrink-0 ${unreadCount > 0 ? "animate-bell-glow" : ""}`}
              >
                <BellIcon />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Button>
            )}
            <div className="flex flex-col items-end">
              <div className="text-right">
              <Menu as="div" className="relative inline-block text-left z-20">
                <div>
                  <Menu.Button className="inline-flex w-full justify-center rounded-md px-4 bg-[#E4EBF5] text-sm font-medium">
                    {userDetails.name || userDetails.sh || userDetails.username || "-"}
                    <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5 hover:text-blue-500" aria-hidden="true" />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
                    <div className="px-1 py-1 ">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => {
                              setChangePassPopup(true);
                            }}
                            className={`${
                              active ? "bg-primary text-white" : "text-gray-900"
                            } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          >
                            Change Password
                          </button>
                        )}
                      </Menu.Item>

                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => {
                              setTotpPopup(true);
                            }}
                            className={`${
                              active ? "bg-primary text-white" : "text-gray-900"
                            } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          >
                            Setup Authenticator
                          </button>
                        )}
                      </Menu.Item>

                      {fingerprintAvailable && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleEnableFingerprint}
                              disabled={fingerprintLoading}
                              className={`${
                                active ? "bg-primary text-white" : "text-gray-900"
                              } group flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm`}
                            >
                              <FingerPrintIcon className="h-4 w-4" />
                              {fingerprintLoading ? "Registering..." : "Enable Fingerprint Login"}
                            </button>
                          )}
                        </Menu.Item>
                      )}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
              </div>

              <div className="flex">
                <small className="text-white font-medium font-inter">
                  {userDetails.role_name == 1
                    ? "Super Admin"
                    : userDetails.role_name == 2
                    ? "Branch Admin"
                    : userDetails.role_name == 3
                    ? "Subbranch Admin"
                    : userDetails.role_name == 4
                    ? "Agent"
                    : userDetails.role_name == 5
                    ? "Accountant"
                    : userDetails.role_name == 8
                    ? "CS"
                    : userDetails.role_name == 9
                    ? "Manager"
                    : userDetails.role_name == 10
                    ? "MIS"
                    : "Customer"}
                </small>
                <small className="text-white ml-1">
                  {branchData.name && `- ${branchData.name}`}
                  {branchData.branch_code && `- ${branchData.branch_code}`}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu sidebar */}
      <Transition show={mobileMenuOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[1000]" onClose={setMobileMenuOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black-900/50" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white pb-4">
                  {/* Mobile menu header */}
                  <div className="flex h-16 shrink-0 items-center justify-between px-4 blue-grad">
                    <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                      <Img src={"/logo-white.png"} className="h-[24px]" />
                    </Link>
                    <button onClick={() => setMobileMenuOpen(false)} className="text-white p-1">
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {/* User info on mobile */}
                  <div className="px-4 py-2 border-b border-gray-200">
                    <p className="font-medium text-gray-900">{userDetails.name || userDetails.sh || userDetails.username || "-"}</p>
                    <p className="text-sm text-gray-500">
                      {userDetails.role_name == 1
                        ? "Super Admin"
                        : userDetails.role_name == 2
                        ? "Branch Admin"
                        : userDetails.role_name == 3
                        ? "Subbranch Admin"
                        : userDetails.role_name == 4
                        ? "Agent"
                        : userDetails.role_name == 5
                        ? "Accountant"
                        : userDetails.role_name == 8
                        ? "CS"
                        : userDetails.role_name == 9
                        ? "Manager"
                        : userDetails.role_name == 10
                        ? "MIS"
                        : "Customer"}
                    </p>
                  </div>

                  {/* Track AWB on mobile */}
                  <div className="px-4">
                    <div className="flex gap-2">
                      <div className="border border-blue-600 rounded-md bg-white flex-1">
                        <Input
                          type="text"
                          className="p-1 w-full"
                          placeholder="Track AWB"
                          value={trackValue}
                          onChange={(e) => {
                            setTrackValue(e);
                          }}
                        />
                      </div>
                      <Button
                        className="bg-blue-600 px-3 py-1 rounded-md text-white"
                        onClick={() => {
                          if (trackValue.length < 10 && userDetails.roleName != "CUSTOMER") {
                            navigator("/pickup/tracking?awb=" + trackValue);
                          } else {
                            navigator((userDetails.roleName == "CUSTOMER" ? "/customer-pod/tracking?awb=" : "/pod/tracking?awb=") + trackValue);
                          }
                          setTrackValue("");
                          setMobileMenuOpen(false);
                        }}
                      >
                        Track
                      </Button>
                    </div>
                  </div>

                  {/* Mobile navigation items */}
                  <nav className="flex flex-1 flex-col px-4">
                    <div className="space-y-1">
                      {mobileNavigation}
                    </div>
                  </nav>

                  {/* Mobile menu footer actions */}
                  <div className="px-4 border-t border-gray-200 pt-4 space-y-2">
                    {userDetails.roleName != "CUSTOMER" && (
                      <button
                        onClick={() => {
                          setIsCalcOpen(true);
                          setMobileMenuOpen(false);
                        }}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100"
                      >
                        <CalculatorIcon className="h-5 w-5" />
                        Tax Calculator
                      </button>
                    )}
                    {userDetails.roleName != "CUSTOMER" && (
                      <button
                        onClick={() => {
                          setIsTodoOpen(true);
                          setMobileMenuOpen(false);
                        }}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100"
                      >
                        <DocumentCheckIcon className="h-5 w-5" />
                        Todo List
                      </button>
                    )}
                    {userDetails.roleName != "CUSTOMER" && (
                    <button
                      onClick={() => {
                        setIsNotificationOpen(true);
                        setMobileMenuOpen(false);
                      }}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100"
                    >
                      <BellIcon className={`h-5 w-5 ${unreadCount > 0 ? "text-red-500" : ""}`} />
                      Notifications
                      {unreadCount > 0 && (
                        <span className="ml-auto bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                          {unreadCount > 9 ? "9+" : unreadCount}
                        </span>
                      )}
                    </button>
                    )}
                    <button
                      onClick={() => {
                        setChangePassPopup(true);
                        setMobileMenuOpen(false);
                      }}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100"
                    >
                      Change Password
                    </button>
                    <button
                      onClick={() => {
                        setTotpPopup(true);
                        setMobileMenuOpen(false);
                      }}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100"
                    >
                      Setup Authenticator
                    </button>
                    {fingerprintAvailable && (
                      <button
                        onClick={() => {
                          handleEnableFingerprint();
                          setMobileMenuOpen(false);
                        }}
                        disabled={fingerprintLoading}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100"
                      >
                        <FingerPrintIcon className="h-5 w-5" />
                        {fingerprintLoading ? "Registering..." : "Enable Fingerprint Login"}
                      </button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Change Password Popup */}
      {changePassPopup && (
        <div className="fixed z-[1002] left-0 top-0 w-full h-full min-h-screen bg-black-900 bg-opacity-20 flex items-start justify-center">
          <div className="rounded-xl border-2 border-primary p-4 bg-white relative mt-24 mx-4 w-full max-w-sm">
            <div className="flex items-center justify-between bg-primary text-white p-2 rounded-lg">
              <p>Change Password: </p>
              <button
                className="px-1"
                onClick={() => {
                  setChangePassPopup(false);
                }}
              >
                ✖
              </button>
            </div>

            <div className="flex flex-col gap-2 mt-4">
              <input
                value={oldPass}
                onChange={(e) => {
                  setOldPass(e.target.value);
                }}
                className="border focus:border-black-900 rounded-md p-2"
                type="password"
                placeholder="Old Password"
              />
              <input
                value={newPass}
                onChange={(e) => {
                  setNewPass(e.target.value);
                }}
                className="border focus:border-black-900 rounded-md p-2"
                type="password"
                placeholder="New Password"
              />
              <input
                value={confirmPass}
                onChange={(e) => {
                  setConfirmPass(e.target.value);
                }}
                className="border focus:border-black-900 rounded-md p-2"
                type="password"
                placeholder="Confirm Password"
              />
              <Button
                onClick={() => {
                  onChangePass();
                }}
                className="border bg-primary text-white py-2 rounded-md"
              >
                Change
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
